import { useState } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../translation/TranslationContext';
import {
  RecoverPasswordOutcome,
  RecoveryEmailCooldown,
  RecoveryEmailSentContent,
} from '../recoverPasswordPanel.types';
import { formatCooldown, normalizeRecoveryEmail } from '../recoverPasswordPanel.utils';
import useSecondsUntil from './useSecondsUntil';

const ONE_SECOND_IN_MILLISECONDS = 1000;

const useRecoverPasswordCooldown = (currentEmail: string) => {
  const { translate } = useTranslation();
  const [cooldowns, setCooldowns] = useState<Record<string, RecoveryEmailCooldown>>({});

  const normalizedEmail = normalizeRecoveryEmail(currentEmail);
  const currentCooldown = cooldowns[normalizedEmail];
  const secondsToRetry = useSecondsUntil(currentCooldown?.retryAt ?? null);

  const registerOutcome = (
    email: string,
    { retryAfterSeconds, isRateLimited }: RecoverPasswordOutcome,
  ) => {
    setCooldowns((previous) => ({
      ...previous,
      [normalizeRecoveryEmail(email)]: {
        retryAt: Date.now() + retryAfterSeconds * ONE_SECOND_IN_MILLISECONDS,
        isRateLimited,
      },
    }));
  };

  const getSentContent = (): RecoveryEmailSentContent | null => {
    if (!currentCooldown) {
      return null;
    }

    if (currentCooldown.isRateLimited) {
      return {
        email: normalizedEmail,
        title: LangEnum.LOGIN_RECOVER_RATE_LIMITED_TITLE,
        message: LangEnum.LOGIN_RECOVER_RATE_LIMITED_HINT,
      };
    }

    return {
      email: normalizedEmail,
      title: LangEnum.LOGIN_RECOVER_SENT_TITLE,
      message: LangEnum.LOGIN_RECOVER_SENT_HINT,
    };
  };

  const resendLabel =
    secondsToRetry > 0
      ? translate(LangEnum.LOGIN_RECOVER_RESEND_IN, { time: formatCooldown(secondsToRetry) })
      : translate(LangEnum.LOGIN_RECOVER_RESEND);

  return {
    sentContent: getSentContent(),
    isCoolingDown: secondsToRetry > 0,
    resendLabel,
    registerOutcome,
  };
};

export default useRecoverPasswordCooldown;
